// Determinar si estamos en entorno local o producción
const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://w0c276cci1.execute-api.us-east-1.amazonaws.com/qa";

export const API_BASE_URL = {
  // Niño:
  childLogin: `${API_BASE}/auth/child/login`,
  childRegister: `${API_BASE}/auth/child/register`,
  childPersonalizar: `${API_BASE}/child/personalizar`,
  childRoom: `${API_BASE}/child/room`,
  childDiaryRoom: `${API_BASE}/child/diary-room`,
  childEmotionRoom: `${API_BASE}/child/emotion-room`,
  childFamilyTree: `${API_BASE}/child/family-tree`,
  childFamilyTreeChat: `${API_BASE}/child/family-tree/chat`,
  childVisibleRooms: `${API_BASE}/child/visible-rooms`,

  // Adulto:
  adultLogin: `${API_BASE}/auth/login`,
  adultRegister: `${API_BASE}/auth/register-adult`,
  adultProfile: `${API_BASE}/adult/profile`,
  adultGrupos: `${API_BASE}/adult/grupos`,
  adultInstituciones: `${API_BASE}/adult/instituciones`,

  // Países:
  saveCountries: `${API_BASE}/countries/save`,
};

const TIMEOUT = 8000; // 8 segundos

// ----------------------------------------------------------------
// 3. Función auxiliar: fetch con timeout
// ----------------------------------------------------------------
const fetchWithTimeout = async (url, options, timeout = TIMEOUT) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  // Asegurarnos de que las opciones de CORS estén correctamente configuradas
  const fetchOptions = {
    ...options,
    signal: controller.signal,
    mode: "cors",
    credentials: "omit", // Cambiamos a 'omit' ya que no necesitamos enviar cookies
  };

  try {
    const response = await fetch(url, fetchOptions);
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

// ----------------------------------------------------------------
// 4. Manejo común de respuestas (JSON vs HTML, errores, status codes)
// ----------------------------------------------------------------
const handleResponse = async (response) => {
  try {
    const contentType = response.headers.get("content-type");
    // Si viene HTML con status ≠ 200 → error de comunicación
    if (
      contentType &&
      contentType.includes("text/html") &&
      response.status !== 200
    ) {
      const error = new Error(
        `Error de comunicación con el servidor (${response.status})`
      );
      error.status = response.status;
      error.isHtmlResponse = true;
      throw error;
    }

    // Intentamos parsear JSON
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(
        data.message ||
          data.error ||
          `Error en la solicitud (${response.status})`
      );
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  } catch (error) {
    // Si ya habíamos marcado isHtmlResponse, lo re-lanzamos
    if (error.isHtmlResponse) throw error;

    // Si no era JSON válido y status es 200 → lanzamos error genérico
    if (error instanceof SyntaxError) {
      const newError = new Error(
        `Error al procesar la respuesta del servidor (${response.status})`
      );
      newError.status = response.status;
      newError.originalError = error;
      newError.isHtmlResponse = true;
      throw newError;
    }
    throw error;
  }
};

// ----------------------------------------------------------------
// 5. LOGIN ADULTO
// ----------------------------------------------------------------
export const login = async (email, password) => {
  // Integrado con backend real
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.adultLogin,
      { method: "POST", headers, body: JSON.stringify({ email, password }) }
    );

    // Si la respuesta vino en HTML pero status 200 → usamos tokens guardados
    const contentType = response.headers.get("content-type");
    if (
      contentType &&
      contentType.includes("text/html") &&
      response.status === 200
    ) {
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      const idToken = localStorage.getItem("idToken");
      if (accessToken) {
        return { accessToken, refreshToken, idToken };
      }
    }

    // Sino, procesamos JSON y devolvemos tokens
    const data = await handleResponse(response);
    if (!data.accessToken) throw new Error("No se recibió token de acceso");
    return data;
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.status === 401) {
      throw new Error("Email o contraseña incorrectos.");
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error(
          "Servicio no encontrado. Por favor, contacta al soporte."
        );
      } else if (error.status === 500) {
        throw new Error(
          "Error interno del servidor. Por favor, intenta más tarde."
        );
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al iniciar sesión");
  }
};

// ----------------------------------------------------------------
// 6. REGISTER ADULTO
// ----------------------------------------------------------------
export const registerAdult = async (data) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.adultRegister,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ ...data, grupos: data.grupos || [] }),
      }
    );

    const result = await handleResponse(response);
    if (!response.ok) {
      const errorMessage =
        result.error || result.message || `Error ${response.status}`;
      throw new Error(errorMessage);
    }
    return { ...result, status: response.status };
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.message.includes("Failed to fetch")) {
      throw new Error(
        "Error de conexión con el servidor. Verifica tu conexión a internet."
      );
    } else if (error.status === 400) {
      throw new Error(
        error.data?.message ||
          "Datos inválidos. Verifica la información ingresada."
      );
    } else if (error.status === 409) {
      throw new Error("El email ya está registrado. Intenta con otro email.");
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error("Servicio no encontrado. Contacta al soporte.");
      } else if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    } else {
      throw new Error(
        error.message || "Error al registrar. Intenta nuevamente."
      );
    }
  }
};

// ----------------------------------------------------------------
// 7. REGISTER NIÑO
// ----------------------------------------------------------------
export const registerChild = async (data) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // Asegurarnos de que los datos estén en el formato correcto
    const requestData = {
      nombre: data.nombre,
      edad: parseInt(data.edad, 10),
      genero: data.genero || "boy",
      avatar: {
        hair: 0,
        mouth: 0,
        shirt: 0,
        gender: data.genero || "boy",
        pants: 0,
        name: data.nombre,
        genderId: data.genero === "girl" ? 1 : 0,
        skin: 0,
        eyes: 0,
        shoes: 0,
      },
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.childRegister,
      {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      }
    );

    const responseData = await handleResponse(response);
    return responseData;
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.status === 400) {
      // Intentar obtener el mensaje de error específico del servidor
      const errorMessage =
        error.data?.message ||
        error.data?.error ||
        "Datos inválidos. Verifica la información ingresada.";
      throw new Error(errorMessage);
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error("Servicio no encontrado. Contacta al soporte.");
      } else if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error registrando niño");
  }
};

const validateChildLoginResponse = (data, nombre) => {
  if (!data) {
    return { valid: false, error: "No se recibió respuesta del servidor" };
  }
  // Detectar si el backend devolvió un error de niño no encontrado
  if (data.error === "Niño no encontrado") {
    return { valid: false, error: "Estás ingresando un usuario que no existe. Intenta nuevamente." };
  }
  if (!data.child_id) {
    return { valid: false, error: "No se recibió el ID del niño" };
  }
  if (!data.nombre) {
    data.nombre = nombre;
  }
  if (!data.avatar) {
    data.avatar = {
      hair: 0,
      mouth: 0,
      shirt: 0,
      gender: "boy",
      pants: 0,
      name: data.nombre,
      genderId: 0,
      skin: 0,
      eyes: 0,
      shoes: 0,
    };
  }
  const requiredAvatarFields = [
    "hair",
    "mouth",
    "shirt",
    "gender",
    "pants",
    "name",
    "genderId",
    "skin",
    "eyes",
    "shoes",
  ];
  const missingFields = requiredAvatarFields.filter(
    (f) => data.avatar[f] === undefined
  );
  if (missingFields.length > 0) {
    if (missingFields.length <= 3) {
      missingFields.forEach((field) => {
        if (field === "name") data.avatar.name = data.nombre;
        else if (field === "gender") data.avatar.gender = "boy";
        else if (field === "genderId") data.avatar.genderId = 0;
        else data.avatar[field] = 0;
      });
    } else {
      return { valid: false, error: "Información del avatar incompleta" };
    }
  }
  return { valid: true, data };
};

// ----------------------------------------------------------------
// 8. LOGIN NIÑO
// ----------------------------------------------------------------
export const loginChild = async (nombre) => {
  try {
    const url = API_BASE_URL.childLogin;

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ nombre }),
    });

    const data = await handleResponse(response);
    const validationResult = validateChildLoginResponse(data, nombre);
    if (validationResult.valid) {
      return validationResult.data;
    }

    // Si la validación falló, lanzar el error directamente
    throw new Error(
      validationResult.error || "No se recibió información del niño"
    );
  } catch (error) {
    // Si el error ya tiene nuestro mensaje personalizado, re-lanzarlo directamente
    if (error.message?.includes("usuario que no existe")) {
      throw error;
    }
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error("Servicio no encontrado. Contacta al soporte.");
      } else if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      }
       else if (error.status === 401) {
        throw new Error("No existe el usuario. Intentá nuevamente.");
      }
      else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    } else if (
      error.status === 401 ||
      error.status === 404 ||
      error.data?.error === "Niño no encontrado" ||
      error.message?.toLowerCase().includes("no encontrado") ||
      error.message?.toLowerCase().includes("not found")
    ) {
      throw new Error("Estás ingresando un usuario que no existe. Intenta nuevamente.");
    }
    throw new Error(error.message || "Error al iniciar sesión");
  }
};

export const saveAvatarToDatabase = async (avatarData) => {
  try {
    const childId = localStorage.getItem("childId");
    if (!childId) throw new Error("No se encontró el ID del niño");

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.childPersonalizar,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ child_id: childId, avatar: avatarData }),
      }
    );
    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error("Servicio no encontrado. Contacta al soporte.");
      } else if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al guardar el avatar");
  }
};

/* Obtener datos de cuarto */
export const getRoomFromDatabase = async (childId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL.childRoom}?child_id=${encodeURIComponent(childId)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Error al recuperar cuarto");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error al recuperar cuarto:", error);
    throw error;
  }
};

/* 
  Guardar Cuarto de niño
*/
export const saveRoomToDatabase = async (wallIndex, itemsArray, itemPositions = {}) => {
  try {
    const childData = JSON.parse(localStorage.getItem("childUser"));
    const childId = childData?.id || localStorage.getItem("childId");

    if (!childId) throw new Error("No se encontró el ID del niño");

    const payload = {
      child_id: childId,
      wall_index: wallIndex,
      room_items: itemsArray,
      item_positions: itemPositions,
    };

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    /* const response = await fetchWithTimeout(API_BASE_URL.childRoom, { */
    const response = await fetchWithTimeout(API_BASE_URL.childRoom, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error("Servicio no encontrado. Contacta al soporte.");
      } else if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al guardar el cuarto");
  }
};

/* Obtener datos del cuarto del diario */
export const getDiaryRoomFromDatabase = async (childId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL.childRoom}?child_id=${encodeURIComponent(childId)}`,
      { method: "GET", headers: { "Content-Type": "application/json", Accept: "application/json" } }
    );
    if (!response.ok) throw new Error("Error al recuperar cuarto del diario");
    const data = await response.json();
    return {
      diary_items:          data.diary_items          ?? [],
      diary_item_positions: data.diary_item_positions ?? {},
    };
  } catch (error) {
    console.error("Error al recuperar cuarto del diario:", error);
    throw error;
  }
};

/* Guardar cuarto del diario */
export const saveDiaryRoomToDatabase = async (itemsArray, itemPositions = {}) => {
  try {
    const childData = JSON.parse(localStorage.getItem("childUser"));
    const childId = childData?.id || localStorage.getItem("childId");
    if (!childId) throw new Error("No se encontró el ID del niño");

    const response = await fetchWithTimeout(API_BASE_URL.childDiaryRoom, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        child_id:              childId,
        diary_items:           itemsArray,
        diary_item_positions:  itemPositions,
      }),
    });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(error.message || "Error al guardar el cuarto del diario");
  }
};

/* Obtener datos del cuarto de emociones */
export const getEmotionRoomFromDatabase = async (childId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL.childRoom}?child_id=${encodeURIComponent(childId)}`,
      { method: "GET", headers: { "Content-Type": "application/json", Accept: "application/json" } }
    );
    if (!response.ok) throw new Error("Error al recuperar cuarto de emociones");
    const data = await response.json();
    return {
      emotion_items:          data.emotion_items          ?? [],
      emotion_item_positions: data.emotion_item_positions ?? {},
    };
  } catch (error) {
    console.error("Error al recuperar cuarto de emociones:", error);
    throw error;
  }
};

/* Guardar cuarto de emociones */
export const saveEmotionRoomToDatabase = async (itemsArray, itemPositions = {}) => {
  try {
    const childData = JSON.parse(localStorage.getItem("childUser"));
    const childId = childData?.id || localStorage.getItem("childId");
    if (!childId) throw new Error("No se encontró el ID del niño");

    const response = await fetchWithTimeout(API_BASE_URL.childEmotionRoom, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        child_id:               childId,
        emotion_items:          itemsArray,
        emotion_item_positions: itemPositions,
      }),
    });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(error.message || "Error al guardar el cuarto de emociones");
  }
};

/* Obtener el árbol familiar guardado */
export const getFamilyTreeFromDatabase = async (childId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL.childRoom}?child_id=${encodeURIComponent(childId)}`,
      { method: "GET", headers: { "Content-Type": "application/json", Accept: "application/json" } }
    );
    if (!response.ok) throw new Error("Error al recuperar el árbol familiar");
    const data = await response.json();
    return data.family_tree ?? null;
  } catch (error) {
    console.error("Error al recuperar el árbol familiar:", error);
    throw error;
  }
};

/* Obtener los cuartos visibles para el niño logueado, según su Howemaker y la config de cada cuarto */
export const getVisibleRooms = async (childId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL.childVisibleRooms}?child_id=${encodeURIComponent(childId)}`,
      { method: "GET", headers: { "Content-Type": "application/json", Accept: "application/json" } }
    );
    if (!response.ok) throw new Error("Error al obtener los cuartos visibles");
    const data = await response.json();
    return data.visible ?? [];
  } catch (error) {
    console.error("Error al obtener los cuartos visibles:", error);
    throw error;
  }
};

/* Guardar el árbol familiar */
export const saveFamilyTreeToDatabase = async (familyTree) => {
  try {
    const childId = localStorage.getItem("childId");
    if (!childId) throw new Error("No se encontró el ID del niño");

    const response = await fetchWithTimeout(API_BASE_URL.childFamilyTree, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        child_id: childId,
        family_tree: familyTree,
      }),
    });
    return await handleResponse(response);
  } catch (error) {
    throw new Error(error.message || "Error al guardar el árbol familiar");
  }
};

/**
 * Guardar países seleccionados por el usuario
 * @param {string} childId - ID del niño
 * @param {Array} countries - Array de países con formato [{id, country, position}]
 */
export const saveCountriesToDatabase = async (childId, countries) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.saveCountries,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ child_id: childId, countries }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 404) {
        throw new Error("Servicio no encontrado. Contacta al soporte.");
      } else if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    console.error("Error al guardar países:", error);
    // No lanzamos error para que la app siga funcionando aunque falle el guardado
    return { success: false, error: error.message };
  }
};

// ----------------------------------------------------------------
// PERFIL DEL ADULTO
// ----------------------------------------------------------------

/**
 * Obtener perfil del adulto
 * @param {string} adultId - ID del adulto
 */
export const getAdultProfile = async (adultId) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultProfile}?adult_id=${adultId}`,
      {
        method: "GET",
        headers,
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.status === 404) {
      throw new Error("Perfil no encontrado.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al obtener el perfil");
  }
};

/**
 * Actualizar perfil del adulto
 * @param {string} adultId - ID del adulto
 * @param {Object} profileData - Datos del perfil a actualizar
 */
export const updateAdultProfile = async (adultId, profileData) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.adultProfile,
      {
        method: "PUT",
        headers,
        body: JSON.stringify({ adult_id: adultId, ...profileData }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.status === 404) {
      throw new Error("Perfil no encontrado.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al actualizar el perfil");
  }
};

// ----------------------------------------------------------------
// GRUPOS DEL ADULTO
// ----------------------------------------------------------------

/**
 * Crear un nuevo grupo
 * @param {string} adultId - ID del adulto
 * @param {Object} grupoData - Datos del grupo a crear
 */
export const createGrupo = async (adultId, grupoData) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.adultGrupos,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ adult_id: adultId, ...grupoData }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al crear el grupo");
  }
};

/**
 * Obtener todos los grupos del adulto
 * @param {string} adultId - ID del adulto
 */
export const getGrupos = async (adultId) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}?adult_id=${adultId}`,
      {
        method: "GET",
        headers,
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al obtener los grupos");
  }
};

/**
 * Actualizar un grupo existente
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo a actualizar
 * @param {Object} grupoData - Datos actualizados del grupo
 */
export const updateGrupo = async (adultId, grupoId, grupoData) => {
  try {
    console.log('📤 updateGrupo called with:');
    console.log('   adultId:', adultId);
    console.log('   grupoId:', grupoId);
    console.log('   grupoData:', grupoData);

    // Codificar el grupoId para que el # no cause problemas en la URL
    const encodedGrupoId = encodeURIComponent(grupoId);
    console.log('   encodedGrupoId:', encodedGrupoId);
    console.log('   URL:', `${API_BASE_URL.adultGrupos}/${encodedGrupoId}`);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}`,
      {
        method: "PUT",
        headers,
        body: JSON.stringify({ adult_id: adultId, ...grupoData }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al actualizar el grupo");
  }
};

/**
 * Eliminar un grupo
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo a eliminar
 */
export const deleteGrupo = async (adultId, grupoId) => {
  try {
    // Codificar el grupoId para que el # no cause problemas en la URL
    const encodedGrupoId = encodeURIComponent(grupoId);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}?adult_id=${adultId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al eliminar el grupo");
  }
};

// ----------------------------------------------------------------
// NIÑOS EN GRUPOS
// ----------------------------------------------------------------

/**
 * Agregar un niño a un grupo
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo
 * @param {Object} childData - Datos del niño
 */
export const addChild = async (adultId, grupoId, childData) => {
  try {
    // Codificar el grupoId para que el # no cause problemas en la URL
    const encodedGrupoId = encodeURIComponent(grupoId);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}/children`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ adult_id: adultId, ...childData }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al agregar el niño");
  }
};

/**
 * Alta masiva de niños a un grupo a partir de filas ya parseadas de un Excel
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo
 * @param {Array} rows - Filas parseadas { nombre, apellido, genero, identificador, mail, usuario }
 */
export const bulkImportChildren = async (adultId, grupoId, rows) => {
  try {
    const encodedGrupoId = encodeURIComponent(grupoId);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}/children/bulk-import`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ adult_id: adultId, rows }),
      },
      30000
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al importar los niños desde Excel");
  }
};

/**
 * Actualizar un niño en un grupo
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo
 * @param {string} childId - ID del niño
 * @param {Object} childData - Datos actualizados del niño
 */
export const updateChild = async (adultId, grupoId, childId, childData) => {
  try {
    // Codificar IDs para que el # no cause problemas en la URL
    const encodedGrupoId = encodeURIComponent(grupoId);
    const encodedChildId = encodeURIComponent(childId);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}/children/${encodedChildId}`,
      {
        method: "PUT",
        headers,
        body: JSON.stringify({ adult_id: adultId, ...childData }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al actualizar el niño");
  }
};

/**
 * Eliminar un niño de un grupo
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo
 * @param {string} childId - ID del niño
 */
export const deleteChild = async (adultId, grupoId, childId) => {
  try {
    // Codificar IDs para que el # no cause problemas en la URL
    const encodedGrupoId = encodeURIComponent(grupoId);
    const encodedChildId = encodeURIComponent(childId);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}/children/${encodedChildId}?adult_id=${adultId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al eliminar el niño");
  }
};

/**
 * Obtener los niños de un grupo específico
 * @param {string} adultId - ID del adulto
 * @param {string} grupoId - ID del grupo
 */
export const getChildren = async (adultId, grupoId) => {
  try {
    // Codificar el grupoId para que el # no cause problemas en la URL
    const encodedGrupoId = encodeURIComponent(grupoId);

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultGrupos}/${encodedGrupoId}/children?adult_id=${adultId}`,
      {
        method: "GET",
        headers,
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al obtener los niños");
  }
};

// ----------------------------------------------------------------
// INSTITUCIONES DEL ADULTO
// ----------------------------------------------------------------

/**
 * Crear una nueva institución
 * @param {string} adultId - ID del adulto
 * @param {Object} institucionData - Datos de la institución a crear
 */
export const createInstitucion = async (adultId, institucionData) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      API_BASE_URL.adultInstituciones,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ adult_id: adultId, ...institucionData }),
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al crear la institución");
  }
};

/**
 * Obtener todas las instituciones del adulto
 * @param {string} adultId - ID del adulto
 */
export const getInstituciones = async (adultId) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const response = await fetchWithTimeout(
      `${API_BASE_URL.adultInstituciones}?adult_id=${adultId}`,
      {
        method: "GET",
        headers,
      }
    );

    return await handleResponse(response);
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("La solicitud tardó demasiado. Intenta nuevamente.");
    } else if (error.isHtmlResponse) {
      if (error.status === 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(
          `Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`
        );
      }
    }
    throw new Error(error.message || "Error al obtener las instituciones");
  }
};

// ===================================================================
// VINCULACIÓN DE NIÑOS
// ===================================================================

export const generateInvitationLink = async (adultId, grupoId, childId) => {
  try {
    const url = `${API_BASE}/adult/grupos/${encodeURIComponent(grupoId)}/children/${encodeURIComponent(childId)}/generate-link`;

    const response = await fetchWithTimeout(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adult_id: adultId }),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    if (error.status) {
      if (error.status === 404) {
        throw new Error("No se encontró el niño o el grupo");
      } else if (error.status >= 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(`Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`);
      }
    }
    throw new Error(error.message || "Error al generar link de invitación");
  }
};

export const validateInvitation = async (token) => {
  try {
    const url = `${API_BASE}/adult/validate-invitation/${encodeURIComponent(token)}`;

    const response = await fetchWithTimeout(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    if (error.status) {
      if (error.status === 400) {
        throw new Error(error.message || "El link de invitación ha expirado o es inválido");
      } else if (error.status === 404) {
        throw new Error("La invitación ya no es válida");
      } else if (error.status >= 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(`Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`);
      }
    }
    throw new Error(error.message || "Error al validar invitación");
  }
};

export const acceptInvitation = async (token, childUserId) => {
  try {
    const url = `${API_BASE}/adult/accept-invitation`;

    const response = await fetchWithTimeout(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, child_user_id: childUserId }),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    if (error.status) {
      if (error.status === 400) {
        throw new Error(error.message || "No se pudo aceptar la invitación");
      } else if (error.status === 404) {
        throw new Error("La invitación ya no es válida");
      } else if (error.status >= 500) {
        throw new Error("Error interno del servidor. Intenta más tarde.");
      } else {
        throw new Error(`Error de comunicación con el servidor (${error.status}). Intenta nuevamente.`);
      }
    }
    throw new Error(error.message || "Error al aceptar invitación");
  }
};

// Obtener información básica de un usuario niño
export const getChildInfo = async (childId) => {
  try {
    const url = `${API_BASE}/child/info?child_id=${encodeURIComponent(childId)}`;

    const response = await fetchWithTimeout(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error('Error al obtener info del niño:', error);
    return null; // Retornar null si falla, para no romper la UI
  }
};

// Guardar tracking de emoción seleccionada por el niño
export const trackEmotion = async (childId, emotionName) => {
  try {
    const url = `${API_BASE}/child/track-emotion`;

    const response = await fetchWithTimeout(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        child_id: childId,
        emotion_name: emotionName
      }),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error('Error al guardar tracking de emoción:', error);
    return null; // No romper la UI si falla el tracking
  }
};

// Guardar tracking de chakra seleccionado por el niño
export const trackChakra = async (childId, chakraId) => {
  try {
    const url = `${API_BASE}/child/track-chakra`;

    const response = await fetchWithTimeout(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        child_id: childId,
        chakra_id: chakraId
      }),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error('Error al guardar tracking de chakra:', error);
    return null; // No romper la UI si falla el tracking
  }
};

// Obtener estadísticas de emociones y chakras de un grupo
export const getInstitucionAdults = async (institucionId, adultId) => {
  try {
    const url = `${API_BASE}/adult/instituciones/${encodeURIComponent(institucionId)}/adults?adult_id=${encodeURIComponent(adultId)}`;
    const response = await fetchWithTimeout(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    return handleResponse(response);
  } catch (error) {
    console.error('Error al obtener adultos de institución:', error);
    throw error;
  }
};

export const shareGrupo = async (sourceAdultId, grupoId, targetAdultIds, targetEmails = []) => {
  try {
    const response = await fetchWithTimeout(`${API_BASE}/adult/grupos/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_adult_id: sourceAdultId,
        grupo_id: grupoId,
        target_adult_ids: targetAdultIds,
        target_emails: targetEmails
      })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Error al compartir grupo:', error);
    throw error;
  }
};

export const getGrupoEstadisticas = async (grupoId, fechaDesde, fechaHasta, language = 'es') => {
  try {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fecha_desde', fechaDesde);
    if (fechaHasta) params.append('fecha_hasta', fechaHasta);
    params.append('language', language);

    const url = `${API_BASE}/adult/grupos/${encodeURIComponent(grupoId)}/estadisticas?${params.toString()}`;

    const response = await fetchWithTimeout(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error('Error al obtener estadísticas del grupo:', error);
    throw error;
  }
};

export const requestPasswordReset = async (email) => {
  const response = await fetchWithTimeout(`${API_BASE}/adult/reset-password/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return handleResponse(response);
};

export const confirmPasswordReset = async (token, newPassword) => {
  const response = await fetchWithTimeout(`${API_BASE}/adult/reset-password/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  return handleResponse(response);
};

// ── HoweCoins ──────────────────────────────────────────────────────

export const fetchCoins = async (child_id) => {
  const response = await fetchWithTimeout(`${API_BASE}/child/coins?child_id=${encodeURIComponent(child_id)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  return handleResponse(response);
};

export const addCoins = async (child_id, amount, reason = '') => {
  const response = await fetchWithTimeout(`${API_BASE}/child/coins/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ child_id, amount, reason }),
  });
  const result = await handleResponse(response);
  window.dispatchEvent(new CustomEvent("howecoin-earned", { detail: { amount } }));
  return result;
};

export const getDiaryEntries = async (child_id) => {
  const response = await fetchWithTimeout(`${API_BASE}/child/diary?child_id=${encodeURIComponent(child_id)}`);
  return handleResponse(response);
};

export const saveDiaryEntry = async (child_id, entry_id, date, pages, photos = []) => {
  const response = await fetchWithTimeout(`${API_BASE}/child/diary/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ child_id, entry_id, date, pages, photos }),
  });
  return handleResponse(response);
};

export const getDiaryFrames = async (child_id) => {
  const response = await fetchWithTimeout(`${API_BASE}/child/diary/frames?child_id=${encodeURIComponent(child_id)}`);
  return handleResponse(response);
};

export const saveDiaryFrames = async (child_id, frames) => {
  const response = await fetchWithTimeout(`${API_BASE}/child/diary/frames/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ child_id, frames }),
  });
  return handleResponse(response);
};

export const getTrustedAdult = async (childId) => {
  try {
    const response = await fetchWithTimeout(
      `${API_BASE}/child/trusted-adult?child_id=${encodeURIComponent(childId)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Error obteniendo adulto de confianza:', error);
    return { trustedAdult: { nombre: '', mail: '' } };
  }
};

export const saveTrustedAdult = async (childId, nombre, mail) => {
  const response = await fetchWithTimeout(`${API_BASE}/child/trusted-adult`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ child_id: childId, nombre, mail }),
  });
  return handleResponse(response);
};

export const getLinkedAdults = async (childId) => {
  try {
    const response = await fetchWithTimeout(
      `${API_BASE}/child/linked-adults?child_id=${encodeURIComponent(childId)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Error obteniendo adultos vinculados:', error);
    return { adults: [] };
  }
};

export const spendCoins = async (child_id, amount, reason = '') => {
  const response = await fetchWithTimeout(`${API_BASE}/child/coins/spend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ child_id, amount, reason }),
  });
  if (response.status === 402) {
    const data = await response.json();
    const err = new Error('insufficient_balance');
    err.howe_coins = data.howe_coins;
    throw err;
  }
  const result = await handleResponse(response);
  window.dispatchEvent(new CustomEvent("howecoin-spent", { detail: { amount } }));
  return result;
};
